/**
 * ============================================================================
 * QRQC 快速反应案例 API 工厂函数
 * ============================================================================
 *
 * 📋 文件职责：
 * - 定义 QRQC（案例/例会/LPA 联动/8D 升级）的全部 API 请求方法
 * - 路由常量统一取自 API_ROUTES.quality.qrqc*
 *
 * @module api/quality/qrqc-factory
 * @author YSFL Team
 * @created 2026-09-20
 * ============================================================================
 */

import type {
  QrqcCase,
  QrqcBoard,
  QrqcCaseQueryParams,
  QrqcLpaLinkedItem,
  QrqcMeeting,
  QrqcMeetingQueryParams,
  QrqcReviewQueue,
  QrqcStatistics,
  AppendQrqcLpaItemsForm,
  CloseQrqcCaseForm,
  CompleteContainmentForm,
  CreateQrqcCaseForm,
  CreateQrqcMeetingItemForm,
  EscalateQrqc8dForm,
  RegisterQrqcMeetingForm,
  RejectQrqcCaseForm,
  UpdateQrqcAnalysisForm,
  UpdateQrqcCaseForm,
  UpdateQrqcDeliverableForm,
  VerifyQrqcCaseForm,
} from '../../types/quality/qrqc'
import type { PaginationResult } from '../../types/api/responses'
import type { IRequestClient } from '../core/client'
import { API_ROUTES } from '../routes'

// ============================================================================
// 工厂函数
// ============================================================================

/**
 * 创建 QRQC 快速反应案例 API 实例
 *
 * @param client - HTTP 请求客户端
 */
export function createQrqcApi(client: IRequestClient) {
  const routes = API_ROUTES.quality

  return {
    // ========================================================================
    // 查询
    // ========================================================================

    /** 案例分页列表 */
    async getCases(params?: QrqcCaseQueryParams): Promise<PaginationResult<QrqcCase>> {
      const res = await client.get<{ items: QrqcCase[]; pagination: PaginationResult<QrqcCase>['pagination'] }>(
        routes.qrqcCases,
        { params },
      )
      return { items: res.items, pagination: res.pagination } as PaginationResult<QrqcCase>
    },

    /** 驾驶舱统计 */
    async getStatistics(): Promise<QrqcStatistics> {
      return client.get<QrqcStatistics>(routes.qrqcStatistics)
    },

    /** 快反看板 */
    async getBoard(limitPerColumn?: number): Promise<QrqcBoard> {
      return client.get<QrqcBoard>(routes.qrqcBoard, { params: { limit_per_column: limitPerColumn } })
    },

    /** 案例详情 */
    async getCase(id: number | string): Promise<QrqcCase> {
      return client.get<QrqcCase>(routes.qrqcCaseDetail(id))
    },

    /** 案例追加的分层审核检查项 */
    async getLpaItems(id: number | string): Promise<{ items: QrqcLpaLinkedItem[] }> {
      return client.get(routes.qrqcCaseLpaItems(id))
    },

    // ========================================================================
    // 创建与基础更新
    // ========================================================================

    /** 创建案例（遏制计时随即开始） */
    async createCase(data: CreateQrqcCaseForm): Promise<QrqcCase> {
      return client.post<QrqcCase>(routes.qrqcCases, data)
    },

    /** 更新案例基础信息与措施 */
    async updateCase(id: number | string, data: UpdateQrqcCaseForm): Promise<QrqcCase> {
      return client.put<QrqcCase>(routes.qrqcCaseDetail(id), data)
    },

    /** 确认遏制完成 */
    async completeContainment(id: number | string, data: CompleteContainmentForm): Promise<QrqcCase> {
      return client.post<QrqcCase>(routes.qrqcCaseContainment(id), data)
    },

    /** 保存分析（根因追问/鱼骨/证据/根因总结） */
    async updateAnalysis(id: number | string, data: UpdateQrqcAnalysisForm): Promise<QrqcCase> {
      return client.put<QrqcCase>(routes.qrqcCaseAnalysis(id), data)
    },

    /** 提交分析（进入待验证；不良品证据必填） */
    async submitAnalysis(id: number | string): Promise<QrqcCase> {
      return client.post<QrqcCase>(routes.qrqcCaseAnalysisSubmit(id))
    },

    /** 完成纠正/预防措施（type = corrective | preventive） */
    async completeAction(id: number | string, type: 'corrective' | 'preventive'): Promise<QrqcCase> {
      return client.post<QrqcCase>(routes.qrqcCaseActionComplete(id, type))
    },

    /** 更新交付项（固化动作） */
    async updateDeliverable(
      id: number | string,
      itemKey: string,
      data: UpdateQrqcDeliverableForm,
    ): Promise<QrqcCase> {
      return client.put<QrqcCase>(routes.qrqcCaseDeliverable(id, itemKey), data)
    },

    // ========================================================================
    // 验证 / 驳回 / 关闭（三权限独立）
    // ========================================================================

    /** 验证通过（进入待关闭） */
    async verifyCase(id: number | string, data: VerifyQrqcCaseForm): Promise<QrqcCase> {
      return client.post<QrqcCase>(routes.qrqcCaseVerify(id), data)
    },

    /** 驳回（退回分析中） */
    async rejectCase(id: number | string, data: RejectQrqcCaseForm): Promise<QrqcCase> {
      return client.post<QrqcCase>(routes.qrqcCaseReject(id), data)
    },

    /** 关闭（校验固化动作等关闭条件） */
    async closeCase(id: number | string, data?: CloseQrqcCaseForm): Promise<QrqcCase> {
      return client.post<QrqcCase>(routes.qrqcCaseClose(id), data)
    },

    // ========================================================================
    // LPA 联动
    // ========================================================================

    /** 追加分层审核检查项（多模板/多层级；同问题暂停项自动复用） */
    async appendLpaItems(id: number | string, data: AppendQrqcLpaItemsForm): Promise<{ items: QrqcLpaLinkedItem[] }> {
      return client.post(routes.qrqcCaseLpaItems(id), data)
    },

    /** 暂停关联检查项（案例关闭后） */
    async pauseLpaItem(id: number | string, itemId: number): Promise<{ item: QrqcLpaLinkedItem }> {
      return client.post(routes.qrqcCaseLpaItemPause(id, itemId))
    },

    // ========================================================================
    // 8D 升级
    // ========================================================================

    /** 发起 8D 草稿（D2-D7 案例字段快照带入） */
    async escalateTo8d(id: number | string, data?: EscalateQrqc8dForm): Promise<{ case: QrqcCase; eight_d_id: number; eight_d_no: string }> {
      return client.post(routes.qrqcCaseEscalate8d(id), data)
    },

    // ========================================================================
    // 例会记录
    // ========================================================================

    /** 例会记录分页列表 */
    async getMeetings(params?: QrqcMeetingQueryParams): Promise<PaginationResult<QrqcMeeting>> {
      const res = await client.get<{ items: QrqcMeeting[]; pagination: PaginationResult<QrqcMeeting>['pagination'] }>(
        routes.qrqcMeetings,
        { params },
      )
      return { items: res.items, pagination: res.pagination } as PaginationResult<QrqcMeeting>
    },

    /** 今日评审清单 */
    async getReviewQueue(): Promise<QrqcReviewQueue> {
      return client.get<QrqcReviewQueue>(routes.qrqcReviewQueue)
    },

    /** 单场例会记录 */
    async getMeeting(id: number | string): Promise<QrqcMeeting> {
      return client.get<QrqcMeeting>(routes.qrqcMeetingDetail(id))
    },

    /** 登记会议记录（可一并提交逐案例评审条目） */
    async registerMeeting(data: RegisterQrqcMeetingForm): Promise<QrqcMeeting> {
      return client.post<QrqcMeeting>(routes.qrqcMeetings, data)
    },

    /** 追加单条案例评审结论（结论即时写回案例） */
    async addMeetingItem(
      id: number | string,
      data: CreateQrqcMeetingItemForm,
    ): Promise<{ item: import('../../types/quality/qrqc').QrqcMeetingItem }> {
      return client.post(routes.qrqcMeetingItems(id), data)
    },
  }
}

export type QrqcApi = ReturnType<typeof createQrqcApi>
